import React, { MouseEvent, ReactNode } from 'react';
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import { ArrowList, OrderList } from 'components/_new/List';
import { Tip } from 'components/userPanel/sessions/components/Tip';
import { Link, LinkProps } from 'components/_new/Link';
import { IconSvg } from 'components/_new/Icon';
import { LinkDoc } from 'components/userPanel/sessions/components/LinkDoc';
import { Blue } from 'components/userPanel/sessions/components/Blue';
import { BlueQuestions } from 'components/userPanel/sessions/components/BlueQuestions';
import { Pink } from 'components/userPanel/sessions/components/Pink';
import { PinkQuestions } from 'components/userPanel/sessions/components/PinkQuestions';
import { MessageBox } from 'components/_new/MessageBox';
import { WhiteboardTemplateKey, WhiteboardTemplateReference } from 'types/Whiteboard';
import { extractWhiteboardTemplateKeyFromPath } from 'utils/whiteboard';
import { scrollElementToById } from './scroll';

const Blank = ({ message }: { message: ReactNode }) => {
	return <MessageBox variant="blank" message={message} />;
};

export type ContentParserOptions = {
	parserOptions?: HTMLReactParserOptions;
	handlers?: Record<string, (...args: any) => void> & {
		onWhiteboardTemplateClick?: (template: WhiteboardTemplateReference) => void;
		onWhiteboardClick?: (templateId: number) => void;
	};
	replaces?: Record<string, any>;
	disableFormat?: boolean;
	forceTargetBlank?: boolean;
};

export const contentParser = (options?: ContentParserOptions) => {
	const { parserOptions, handlers, replaces, disableFormat, forceTargetBlank } = {
		parserOptions: {},
		handlers: {},
		replaces: {},
		disableFormat: false,
		forceTargetBlank: false,
		...options,
	};
	const parserReplace = parserOptions?.replace as
		((domNode: any, index: number) => ReturnType<NonNullable<HTMLReactParserOptions['replace']>>) | undefined;

	const replace = (domNode: any, index?: number) => {
		const rawDomNodeName = String(domNode.name || '').toLowerCase();
		const domNodeName = rawDomNodeName.split('_')[0];

		if (parserReplace) {
			const replaced = parserReplace(domNode, index ?? 0);
			if (replaced !== undefined) {
				return replaced;
			}
		}

		if (!disableFormat) {
			if (domNodeName === 'div' && domNode.attribs?.tip !== undefined && domNode.attribs.tip === '') {
				return <Tip message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (domNodeName === 'div' && domNode.attribs?.blue !== undefined && domNode.attribs.blue === '') {
				return <Blue message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (
				domNodeName === 'div' &&
				domNode.attribs?.blue_questions !== undefined &&
				domNode.attribs.blue_questions === ''
			) {
				return <BlueQuestions message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (domNodeName === 'div' && domNode.attribs?.pink !== undefined && domNode.attribs.pink === '') {
				return <Pink message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (
				domNodeName === 'div' &&
				domNode.attribs?.pink_questions !== undefined &&
				domNode.attribs.pink_questions === ''
			) {
				return <PinkQuestions message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (domNodeName === 'ul') {
				const list = <ArrowList>{domToReact(domNode.children, finalParserOptions)}</ArrowList>;
				if (domNode.attribs?.blue !== undefined && domNode.attribs.blue === '') {
					return <Blue message={list} />;
				}
				if (domNode.attribs?.blue_questions !== undefined && domNode.attribs.blue_questions === '') {
					return <BlueQuestions message={list} />;
				}
				if (domNode.attribs?.pink !== undefined && domNode.attribs.pink === '') {
					return <Pink message={list} />;
				}
				if (domNode.attribs?.pink_questions !== undefined && domNode.attribs.pink_questions === '') {
					return <PinkQuestions message={list} />;
				}
				return <Blank message={list} />;
			}
			if (domNodeName === 'ol') {
				return <OrderList>{domToReact(domNode.children, finalParserOptions)}</OrderList>;
			}
		}

		if (domNodeName === 'a') {
			const rawHref = String(domNode.attribs?.href || '');
			const href = rawHref.replace(/[\r\n\t]+/g, '').trim();
			const getAttribute = (name: string) =>
				Object.entries(domNode.attribs || {}).find(([key]) => key.toLowerCase() === name.toLowerCase())?.[1];
			const whiteboardTemplateIdAttribute = getAttribute('whiteboardTemplateId') || getAttribute('whiteboard');
			const parsedWhiteboardTemplateId = Number(whiteboardTemplateIdAttribute);
			const whiteboardTemplateId =
				Number.isInteger(parsedWhiteboardTemplateId) && parsedWhiteboardTemplateId > 0
					? parsedWhiteboardTemplateId
					: undefined;
			const whiteboardTemplateLabel = String(getAttribute('whiteboardTemplateLabel') || '').trim() || undefined;
			const hashReference = href.match(/#(?:whiteboardTemplateId|whiteboardTemplateLabel)_([^?&]+)/i);
			const hashName = href.match(/#(whiteboardTemplateId|whiteboardTemplateLabel)_/i)?.[1].toLowerCase();
			const hashValue = hashReference?.[1] ? decodeURIComponent(hashReference[1]).trim() : undefined;
			let hashTemplateReference: WhiteboardTemplateReference | undefined;
			if (hashName === 'whiteboardtemplateid' && hashValue && Number.isInteger(Number(hashValue))) {
				hashTemplateReference = { id: Number(hashValue) };
			} else if (hashName === 'whiteboardtemplatelabel' && hashValue) {
				hashTemplateReference = { label: hashValue };
			}
			const whiteboardTemplateKey = extractWhiteboardTemplateKeyFromPath(href) as WhiteboardTemplateKey | null;
			let whiteboardTemplateReference: WhiteboardTemplateReference | undefined;
			if (whiteboardTemplateId) {
				whiteboardTemplateReference = { id: whiteboardTemplateId };
			} else if (whiteboardTemplateLabel) {
				whiteboardTemplateReference = { label: whiteboardTemplateLabel };
			} else if (hashTemplateReference) {
				whiteboardTemplateReference = hashTemplateReference;
			} else if (whiteboardTemplateKey) {
				whiteboardTemplateReference = { key: whiteboardTemplateKey };
			}
			const isArticleLink =
				(domNode.attribs?.article !== undefined && domNode.attribs.article !== '') ||
				href.includes('/knowledge-base/article/') ||
				href.includes('knowledge-base/article/');
			const linkProps: LinkProps = {
				to: href,
				className: whiteboardTemplateReference ? 'whiteboard-template-link' : undefined,
				variant: whiteboardTemplateReference ? 'purple' : undefined,
				preventDefaultOnClick: Boolean(whiteboardTemplateReference),
				onClick: (event: MouseEvent<HTMLAnchorElement>) => {
					if (
						domNode.attribs?.openautoreflection !== undefined &&
						domNode.attribs.openautoreflection === ''
					) {
						scrollElementToById('scrollWrapperRef');
						scrollElementToById('pageScrollWrapperRef');
						handlers?.openAutoreflectionDialog?.();
						// event.preventDefault();
					}

					if (domNode.attribs?.scrolltotop !== undefined && domNode.attribs.scrolltotop === '') {
						scrollElementToById('scrollWrapperRef');
						scrollElementToById('pageScrollWrapperRef');
						// event.preventDefault();
					}
					if (isArticleLink) {
						const articleMatch = href.match(/knowledge-base\/article\/(\d+)/);
						const lastLinkPart = href.split('/').pop()?.split('-')[0];
						const articleId = parseInt(
							domNode.attribs?.article || articleMatch?.[1] || lastLinkPart || '',
							10
						);
						if (articleId && !Number.isNaN(articleId) && handlers?.onArticleLinkClick) {
							event.preventDefault();
							handlers.onArticleLinkClick(articleId);
						}
					}
					if (whiteboardTemplateReference) {
						event.preventDefault();
						if (handlers?.onWhiteboardTemplateClick) {
							handlers.onWhiteboardTemplateClick(whiteboardTemplateReference);
						} else if (whiteboardTemplateReference.id && handlers?.onWhiteboardClick) {
							handlers.onWhiteboardClick(whiteboardTemplateReference.id);
						}
					}
				},
			};

			if (forceTargetBlank) {
				linkProps.target = '_blank';
				linkProps.rel = 'noopener noreferrer';
			}

			const hasImage = domNode.children?.some((child: any) => child.name === 'img');
			if (whiteboardTemplateReference && !hasImage) {
				return (
					<span className="inline">
						<Link target={domNode.attribs?.target} {...linkProps}>
							<IconSvg
								name="diagram-square"
								className="icon-container icon-container-xs mr-2"
								style={{ verticalAlign: 'middle' }}
							/>
							{domToReact(domNode.children, finalParserOptions)}
						</Link>
					</span>
				);
			}
			if (isArticleLink && !hasImage) {
				return (
					<span className="inline">
						<LinkDoc {...linkProps} to={isArticleLink && handlers?.onArticleLinkClick ? '#' : linkProps.to}>
							{domToReact(domNode.children, finalParserOptions)}
						</LinkDoc>
						<Link to={linkProps.to} target="_blank" icon="external-link-alt" iconStyle="solid" iconOnly />
					</span>
				);
			}

			return (
				<Link target={domNode.attribs?.target} {...linkProps}>
					{domToReact(domNode.children, finalParserOptions)}
				</Link>
			);
		}
		if (parserReplace) {
			return parserReplace(domNode, index ?? 0);
		}
		return domNode;
	};

	const finalParserOptions = { ...parserOptions, replace };

	return {
		parse: (_content: string) => {
			let content = _content;

			const replaceEntries = Array.from(Object.entries(replaces || {}));
			if (replaceEntries.length > 0) {
				content = replaceEntries.reduce(
					(current, [name, replacement]) =>
						current.replace(new RegExp(`{{\\s*${name}\\s*}}`, 'g'), String(replacement ?? '')),
					content
				);
			}

			// Clean line breaks inside href and src attributes
			content = content.replace(/href=["']([^"']*)["']/gi, (match, url) => {
				const cleanedUrl = url.replace(/[\r\n\t]+/g, '').trim();
				return `href="${cleanedUrl}"`;
			});

			// Normalize HTML tags: replace \r\n and multiple whitespace immediately after opening tag <a\r\n -> <a
			content = content.replace(/<([a-zA-Z0-9_-]+)[\r\n\t\s]+/g, '<$1 ').replace(/[\r\n\t]+/g, ' ');

			return parse(content, finalParserOptions);
		},
	};
};
